import React from 'react';
import styled, { css } from 'styled-components';
import { PExtraSmall } from '../../elements/Text';
import { NonCopyableMarkdown, SectionWrapper } from '../common';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';

export default function MultipleRatingSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'multiple-rating' }
  >;
  stepId: string;
}) {
  const { answer, questions, content } = section;
  const [, dispatch] = React.useContext(AssessmentTestContext);

  const setAnswer = React.useCallback(
    (value: Array<{ id: string; label: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_MULTIPLE_RATING',
        payload: { stepId, sectionId: section.id, answer: value },
      });
    },
    [stepId, section.id, dispatch]
  );

  const getSelectedOption = React.useCallback(
    (questionId: string) =>
      answer.find((a) => a.id === questionId)?.label ?? null,
    [answer]
  );

  const selectOption = React.useCallback(
    (questionId: string, label: string) => {
      const others = answer.filter((a) => a.id !== questionId);
      setAnswer([...others, { id: questionId, label }]);
    },
    [answer, setAnswer]
  );

  return (
    <SectionWrapper>
      <NonCopyableMarkdown md={content} />

      <QuestionsContainer>
        {questions.map((q) => (
          <QuestionBlock key={q.id}>
            <NonCopyableMarkdown md={q.content} />
            <RatingRow>
              {q.options.map((option, index) => (
                <RatingCell key={option}>
                  <RatingButton
                    onClick={() => selectOption(q.id, option)}
                    isSelected={getSelectedOption(q.id) === option}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectOption(q.id, option);
                      }
                    }}
                  >
                    {index + 1}
                  </RatingButton>
                </RatingCell>
              ))}
            </RatingRow>
            <LabelsRow>
              {q.options.map((option) => (
                <ScaleLabelText key={option}>{option}</ScaleLabelText>
              ))}
            </LabelsRow>
          </QuestionBlock>
        ))}
      </QuestionsContainer>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 38px;
`;

const QuestionBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const RatingRow = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 6px;
  margin-top: 32px;
  padding: 0 7%;
`;

const RatingCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 25%;
  line-height: 0;
`;

const RatingButton = styled.div<{ isSelected: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 500;
  font-size: 18px;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.3s ease-in-out;
  padding-bottom: 0;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.buttonPrimary};
      background-color: ${theme.colors.buttonPrimary};
      color: ${theme.colors.buttonTextColor};
    `}

  &:hover {
    border-color: ${({ theme }) => theme.colors.buttonPrimary};
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.buttonPrimary : theme.colors.bgActive};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.buttonPrimary};
    outline-offset: 2px;
  }
`;

const LabelsRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-around;
  padding: 0 7%;
`;

const ScaleLabelText = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 400;
  flex: 0 0 20%;
  text-align: center;
  margin-top: 12px;
`;
